import React, {useEffect, useState} from "react";
import {Input, Modal, Select, Tabs, message} from "antd";
import {useTranslation} from "react-i18next";
import DeviceAnalysis from "./DeviceAnalysisComponents/DeviceSection/DeviceAnalysis";
import DeviceMarketAnalysis from "./DeviceAnalysisComponents/MarketSection/DeviceMarketAnalysis";
import DMapComponents from "../../../components/DeviceStatus/DeviceMapComponents/DMapComponents/DMapComponents";
import "./DeviceAnalysisComponents/BaseDeviceAnalysis.css";

const DEVICE_ANALYSIS_TABS = [
    {key: "device", label: "Device Status"},
    {key: "market", label: "Market Status"},
    {key: "maps", label: "Maps"},
];

const getStoredTabs = () => {
    try {
        const parsedTabs = JSON.parse(localStorage.getItem("deviceAnalysisTabs"));
        const validTabs = Array.isArray(parsedTabs)
            ? parsedTabs.filter((tabKey) => DEVICE_ANALYSIS_TABS.some((tab) => tab.key === tabKey))
            : [];

        return validTabs.length ? Array.from(new Set(["device", ...validTabs])) : ["device"];
    } catch {
        return ["device"];
    }
};

const MainDeviceAnalysis = () => {
    const {t} = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [isAddTabModalOpen, setIsAddTabModalOpen] = useState(false);
    const [selectedTabToAdd, setSelectedTabToAdd] = useState(null);
    const [openTabs, setOpenTabs] = useState(getStoredTabs);
    const [activeTab, setActiveTab] = useState(() => {
        const storedActiveTab = localStorage.getItem("activeDeviceTab");
        return DEVICE_ANALYSIS_TABS.some((tab) => tab.key === storedActiveTab) ? storedActiveTab : "device";
    });

    useEffect(() => {
        if (!openTabs.includes(activeTab)) {
            setActiveTab(openTabs[0]);
            return;
        }

        localStorage.setItem("deviceAnalysisTabs", JSON.stringify(openTabs));
        localStorage.setItem("activeDeviceTab", activeTab);
    }, [activeTab, openTabs]);

    const missingTabs = DEVICE_ANALYSIS_TABS.filter((tab) => !openTabs.includes(tab.key));

    const tabItems = openTabs.map((tabKey) => {
        const tabConfig = DEVICE_ANALYSIS_TABS.find((tab) => tab.key === tabKey);

        return {
            key: tabKey,
            label: t(tabConfig.label),
            closable: tabKey !== "device",
            children: null
        };
    });

    const handleEditTabs = (targetKey, action) => {
        if (action === "add") {
            if (!missingTabs.length) {
                message.info(t("All analysis tabs are already open."));
                return;
            }

            setSelectedTabToAdd(missingTabs[0].key);
            setIsAddTabModalOpen(true);
            return;
        }

        if (action === "remove") {
            removeTab(targetKey);
        }
    };

    const removeTab = (targetKey) => {
        if (targetKey === "device") {
            return;
        }

        const targetIndex = openTabs.indexOf(targetKey);
        const nextTabs = openTabs.filter((tabKey) => tabKey !== targetKey);

        setOpenTabs(nextTabs);

        if (activeTab === targetKey) {
            setActiveTab(nextTabs[targetIndex - 1] || nextTabs[0]);
        }
    };

    const addSelectedTab = () => {
        if (!selectedTabToAdd || openTabs.includes(selectedTabToAdd)) {
            return;
        }

        setOpenTabs((prevTabs) => [...prevTabs, selectedTabToAdd]);
        setActiveTab(selectedTabToAdd);
        setSelectedTabToAdd(null);
        setIsAddTabModalOpen(false);
    };

    const closeAddTabModal = () => {
        setIsAddTabModalOpen(false);
        setSelectedTabToAdd(null);
    };

    const renderContent = () => {
        switch (activeTab) {
            case "device":
                return <DeviceAnalysis searchTerm={searchTerm}/>;
            case "market":
                return <DeviceMarketAnalysis searchTerm={searchTerm}/>;
            case "maps":
                return <DMapComponents/>;
            default:
                return null;
        }
    };

    useEffect(() => {
        const activeTabSupportsSearch = activeTab === "device" || activeTab === "market";

        if (!activeTabSupportsSearch && searchTerm) {
            setSearchTerm("");
        }
    }, [activeTab, searchTerm]);

    return (
        <div className="dashboard-container">
            <h1 className="dashboard-title">{t("Device Analysis Center")}</h1>

            <Tabs
                activeKey={activeTab}
                className="analysis-tabs"
                items={tabItems}
                onChange={setActiveTab}
                onEdit={handleEditTabs}
                type="editable-card"
            />

            <div className="top-controls-bar">
                <div className="search-wrapper">
                    {(activeTab === "device" || activeTab === "market") && (
                        <Input
                            placeholder={t("Search by company name...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    )}
                </div>
            </div>

            {renderContent()}

            <Modal
                title={t("Content of new Tab")}
                open={isAddTabModalOpen}
                onCancel={closeAddTabModal}
                onOk={addSelectedTab}
                okButtonProps={{disabled: !selectedTabToAdd}}
                okText={t("Add tab")}
                cancelText={t("Cancel")}
            >
                <Select
                    className="analysis-tab-select"
                    value={selectedTabToAdd}
                    onChange={setSelectedTabToAdd}
                    options={missingTabs.map((tab) => ({
                        value: tab.key,
                        label: t(tab.label)
                    }))}
                    placeholder={t("Select analysis component")}
                />
            </Modal>
        </div>
    );
};

export default MainDeviceAnalysis;
